import { useClipboard, useDisclosure, useHover } from "@mantine/hooks";
import { IconCopy } from "@tabler/icons-react";

import ActionButton from "../../ActionButton";
import { ItemView } from "../../sharedItems";
import type { ItemRoleNullable, ItemType } from "../../utils";

import DeleteItemButton from "./DeleteItemButton";
import EditItemButton from "./EditItemButton";
import classes from "./Item.module.css";
import type { AiPersonalityQuery } from "./queries";
import ReorderItemsButtons from "./ReorderItemsButtons";
import SetReferenceFromItemButton from "./SetReferenceFromItemButton";

type ITuneThread = Exclude<AiPersonalityQuery["aiPersonality"], null>["tuneThreads"]["edges"][0]["node"];

interface IProps {
    item: {
        id: number;
        type: ItemType;
        role: ItemRoleNullable;
        toolCallId: string | null;
        toolName: string | null;
        toolArguments: string | null;
        content: string;
        context: boolean;
        imageB64?: string | null;
        imageName?: string | null;
        retrievalContext?: {
            keywordQuery: string;
            semanticQuery: string;
            hydePassage: string;
            chunks: string[];
            avgRelevance: number;
            documentNames: string[];
        } | null;
        readContext?: {
            documentId: number;
            documentName: string;
            startPart: number;
            endPart: number;
            totalParts: number;
        } | null;
        reasoningSummary?: string[] | null;
    };
    tuneThread: ITuneThread;
}

function Item({ item, tuneThread }: IProps) {
    const [opened, { open, close }] = useDisclosure(!item.context);

    const { hovered, ref } = useHover<HTMLDivElement>();
    const clipboard = useClipboard();

    const copy = () => {
        clipboard.copy(item.content);
    };

    const actionButtons = (
        <>
            {item.role === "ASSISTANT" && item.type === "MESSAGE" && (
                <SetReferenceFromItemButton
                    tuneThreadId={tuneThread.id}
                    itemContent={item.content}
                    llmJudge={tuneThread.llmJudge}
                    currentReferenceAnswer={tuneThread.referenceAnswer}
                />
            )}
            {!!item.id && <DeleteItemButton tuneThreadId={tuneThread.id} tuneThreadItemId={item.id} />}
            {!!item.id && <EditItemButton tuneThreadItem={item} />}
            {!!item.id && (
                <ReorderItemsButtons
                    tuneThread={tuneThread}
                    tuneThreadItemId={item.id}
                    tuneThreadItems={tuneThread.tuneThreadItems.edges.map(({ node }) => node)}
                />
            )}
            <ActionButton onClick={copy} label="Copy content" icon={<IconCopy stroke={1} />} />
        </>
    );

    const effectiveActionButtons = item.type === "MESSAGE" ? actionButtons : hovered ? actionButtons : null;

    const file = item.imageB64
        ? {
              fileType: "PNG",
              fileName: item.imageName || "Image",
              src: item.imageB64,
          }
        : null;

    return (
        <ItemView
            item={{ ...item, file }}
            opened={opened}
            onOpen={open}
            onClose={close}
            actionButtons={effectiveActionButtons}
            containerClassName={classes.chatBox}
            showActions={hovered}
            containerRef={ref}
        />
    );
}

export default Item;
